import { CloseOutlined } from "@ant-design/icons";
import {
  CircularProgress,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "../../../../components/element/Loader";
import NoDataPlaceholder from "../../../../components/element/NoDataPlaceholder";
import SnackbarAlert from "../../../../components/element/SnackbarAlert";
import TableButton from "../../../../components/element/TableButton";
import {
  AIRPORT_FEES_TYPE,
  COMMERCIAL_INVOICE_STATUS,
  DATE_FORMAT,
  FeeType,
  getPaymentMethodDisplay,
  INVOICE_BREADCRUMB_SCREEN_NAME,
  INVOICE_PAYMENT_METHOD,
  INVOICE_STATUS,
} from "../../../../utils/constants";
import ROUTE_PATHS from "../../../../utils/routePaths";
import { formatNumber, formatToTwoDecimal, getAirportData } from "../../../../utils/utils";
import "../InvoiceScreen.css";
import PaymentTable from "./PaymentTable";

// Helper function to determine payment status based on remaining amount
const getPaymentStatus = (remainingAmount) => {
  if (remainingAmount === 0) {
    return 'Paid';
  } else if (remainingAmount < 0) {
    return 'Overpaid';
  } else {
    return 'Partial Paid';
  }
};

function PaidTable({
  invoices,
  airportData,
  isLoading,
  selectedAirlineId,
  currentPage,
  onDownloadInvoice,
  downloadingInvoiceId,
}) {
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "error",
  });

  const handleOpen = (row) => {
    // Close any existing snackbar first
    setSnackbar({ open: false, message: "", type: "error" });

    if (!row.payments || row.payments.length === 0) {
      setTimeout(() => {
        setSnackbar({
          open: true,
          message: "No payment receipt found!",
          type: "error",
        });
      }, 100);
      return;
    }
    setSelectedRow(row);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  const handleViewInvoice = (row) => {
    const enrichedInvoiceData = {
      ...row,
      invoiceStatus: INVOICE_STATUS.PAID,
      id: row.id,
    };
    if (row.feeType === FeeType.LAND_LEASE || row.feeType === FeeType.OFFICE_SPACE || row.feeType === FeeType.RESTAURANT || row.feeType === FeeType.COUNTER_SPACE) {
      navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE_CONCESSION_FEE_DETAILS, {
        state: {
          invoiceData: enrichedInvoiceData,
          selectedAirlineId: selectedAirlineId,
          id: row.id,
          currentPage: currentPage,
        },
      });
    } else {
      navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE_DETAILS, {
        state: {
          invoiceData: enrichedInvoiceData,
          selectedAirlineId: selectedAirlineId,
          id: row.id,
          currentPage: currentPage,
        },
      });
    }
  };

  const getFeeTypeName = (value) => {
    const feeType = AIRPORT_FEES_TYPE.find((fee) => fee.value === value);
    return feeType ? feeType.name : value;
  };

  if (isLoading) return <Loader />;

  if (invoices && invoices?.length === 0) {
    return <NoDataPlaceholder />;
  }

  return (
    <Box sx={{ overflowX: "auto" }}>
      <TableContainer component={Paper} className="invoices-data-table">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>No</TableCell>
              <TableCell>Invoice No</TableCell>
              <TableCell>Invoice Date</TableCell>
              <TableCell>Invoice Amount</TableCell>
              <TableCell>Invoice Due Date</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>View Invoice</TableCell>
              <TableCell>Payment Receipt</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>Payment Date</TableCell>
              <TableCell>Approved Date</TableCell>
              <TableCell>Airport</TableCell>
              <TableCell>Type of Fees</TableCell>
              <TableCell>Customer Number</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((row, index) => (
              <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{row.dynamicsInvoiceNumber}</TableCell>
                <TableCell>
                  {dayjs(row.invoiceDate).format(DATE_FORMAT)}
                </TableCell>
                <TableCell>{formatToTwoDecimal(row.invoiceAmount)}</TableCell>
                <TableCell>{dayjs(row.dueDate).format(DATE_FORMAT)}</TableCell>
                <TableCell>{row.currency}</TableCell>
                <TableCell>
                  <TableButton onClick={() => handleViewInvoice(row)}>
                    View
                  </TableButton>
                </TableCell>
                <TableCell>
                  <TableButton onClick={() => handleOpen(row)}>
                    View
                  </TableButton>
                </TableCell>
                <TableCell>
                  {
                    INVOICE_PAYMENT_METHOD.find(
                      (type) => type.value === row.paymentMethod
                    )?.name
                  }
                </TableCell>
                <TableCell>
                  {dayjs(row.paymentDate).format(DATE_FORMAT)}
                </TableCell>
                <TableCell>
                  {dayjs(row.settlementDate).format(DATE_FORMAT)}
                </TableCell>
                <TableCell className="airport-cell">
                  {row.lineItems && row.lineItems.length > 0
                    ? row.lineItems
                      .map(item => getAirportData(airportData, item.airportId)?.name)
                      .filter(Boolean)
                      .join(", ")
                    : "---"}
                </TableCell>
                <TableCell>{getFeeTypeName(row.feeType)}</TableCell>
                <TableCell>{row.dynamicsCustomerNumber}</TableCell>
                <TableCell>{INVOICE_BREADCRUMB_SCREEN_NAME[row.commercialInvoiceStatus]}</TableCell>
                <TableCell>
                  <TableButton 
                    onClick={() => onDownloadInvoice(row)}
                    disabled={
                      // Disable if already downloading or invoice is not settled
                      downloadingInvoiceId === row.dynamicsInvoiceId ||
                      row.commercialInvoiceStatus !==
                        COMMERCIAL_INVOICE_STATUS.SETTLED_INVOICE
                    }
                  >
                    {downloadingInvoiceId === row.dynamicsInvoiceId ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      'Download'
                    )}
                  </TableButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Modal
        open={modalOpen}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box className="payment-modal-box">
          <CloseOutlined
            className="payment-modal-close"
            onClick={handleClose}
          />
          <Typography className="payment-modal-title" variant="h6" gutterBottom>
            Payment Receipt
          </Typography>
          {selectedRow && (
            <PaymentTable
              rows={selectedRow.payments?.map((payment, index) => ({
                invoiceNo: payment.dynamicsInvoiceNumber,
                paymentMethod: getPaymentMethodDisplay(payment.paymentMethod),
                prn: payment.transactionNumber,
                grandTotal: `$ ${formatNumber(payment.paidAmount)}`,
                Status: getPaymentStatus(payment.remainingAmount),
              })) || []}
              paymentMethod={selectedRow.paymentMethod}
              invoiceData={selectedRow}
            />
          )}
        </Box>
      </Modal>
      {snackbar.open && (
        <SnackbarAlert
          type={snackbar.type}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </SnackbarAlert>
      )}
    </Box>
  );
}

export default PaidTable;
